import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  App as AntdApp,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';

import {
  createOperator,
  deleteOperator,
  listOperators,
  updateOperator,
} from '../api/endpoints';
import type { Operator, Status } from '../api/types';
import { useSingleSubmit } from '../hooks/useSingleSubmit';
import { LIMITS } from '../data/limits';

const { Title, Text } = Typography;

/** Admin regression 6 Oct — the columns' widths added up (Name 240, Email 240,
 *  Phone 170, Address 260, Aircraft 90, Status 110, actions 120). */
const OPERATOR_TABLE_WIDTH = 1230;

export function OperatorsListPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<Status | undefined>(undefined);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<Operator | null>(null);
  const [form] = Form.useForm();
  const { message, modal } = AntdApp.useApp();
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ['operators', q, status, page],
    queryFn: () => listOperators({ q: q || undefined, status, page, pageSize }),
  });

  const createMut = useMutation({
    mutationFn: createOperator,
    onSuccess: () => {
      message.success('Operator created');
      setDrawerOpen(false);
      qc.invalidateQueries({ queryKey: ['operators'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Create failed'),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) => updateOperator(id, body),
    onSuccess: (res: any) => {
      // SHAR-51 — say how many aircraft went inactive with the operator.
      const off = res?.deactivatedAircraft;
      message.success(
        off ? `Operator deactivated; ${off} aircraft deactivated with it` : 'Operator updated',
      );
      setDrawerOpen(false);
      qc.invalidateQueries({ queryKey: ['operators'] });
      if (off) qc.invalidateQueries({ queryKey: ['aircraft'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Update failed'),
  });
  const deleteMut = useMutation({
    mutationFn: deleteOperator,
    onSuccess: () => {
      message.success('Operator deleted');
      qc.invalidateQueries({ queryKey: ['operators'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Delete failed'),
  });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ status: 'active' });
    setDrawerOpen(true);
  };
  const openEdit = (op: Operator) => {
    setEditing(op);
    form.setFieldsValue({
      name: op.name,
      dba: op.dba ?? '',
      email: op.email,
      address: op.address ?? '',
      phone: op.phone ?? '',
      status: op.status,
    });
    setDrawerOpen(true);
  };

  // Regression 28 Sep — one submit at a time (a double click created the operator twice).
  const submitGuard = useSingleSubmit();
  const submit = submitGuard.wrap(async () => {
    const v = await form.validateFields();
    const body = {
      name: v.name,
      dba: v.dba || null,
      email: v.email,
      address: v.address || null,
      phone: v.phone || null,
      status: v.status,
    };
    if (editing && editing.status !== 'inactive' && body.status === 'inactive') {
      // SHAR-51 — deactivating cascades to the aircraft, and re-activating does
      // not undo that, so it is confirmed first.
      modal.confirm({
        title: 'Deactivate this operator?',
        content: `All of its aircraft will be deactivated too and will no longer be offered to customers. Existing bookings are not changed. Re-activating the operator later does not re-activate the aircraft; each one has to be switched on again.`,
        okText: 'Deactivate',
        okButtonProps: { danger: true },
        // Returning the promise keeps the dialog's OK button loading until the
        // server answers, so it cannot be pressed twice either.
        onOk: () => updateMut.mutateAsync({ id: editing.id, body }).catch(() => undefined),
      });
      return;
    }
    if (editing) await updateMut.mutateAsync({ id: editing.id, body });
    else await createMut.mutateAsync(body as any);
  });

  return (
    <>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>Operators</Title>
            <Text type="secondary">Carriers running the fleet that ShareJet brokers seats on.</Text>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New operator
          </Button>
        </div>

        <Card>
          <Space wrap style={{ marginBottom: 16 }}>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search by name, DBA, email"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              style={{ width: 320 }}
              allowClear
            />
            <Select
              placeholder="Status"
              value={status}
              onChange={(v) => {
                setStatus(v);
                setPage(1);
              }}
              allowClear
              style={{ width: 160 }}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
            />
          </Space>
          <Table<Operator>
            rowKey="id"
            loading={list.isLoading}
            dataSource={list.data?.items || []}
            // Admin regression 6 Oct — at 1024 and below the unsized Email,
            // Phone and Address columns were squeezed to 45px, and at 768 the
            // whole page scrolled sideways. Every column now has a width and
            // the table scrolls inside its card once they no longer fit.
            scroll={{ x: OPERATOR_TABLE_WIDTH }}
            pagination={{
              current: page,
              total: list.data?.total || 0,
              pageSize,
              onChange: setPage,
              showTotal: (total) => `${total} operators`,
            }}
            columns={[
              // Regression 27 Sep - a long name ("QA BlueRidge Aviation GmbH") ran over
              // the Email column: the ellipsis sat inside a Space item, which has no
              // width, so it never engaged. The column now has a width and each line
              // is a block of that width, cut with "…" and the full text on hover.
              { title: 'Name', dataIndex: 'name', key: 'name', width: 240, render: (v, r) => (
                <div style={{ maxWidth: 220 }}>
                  <Text strong ellipsis={{ tooltip: v }} style={{ display: 'block' }}>{v}</Text>
                  {r.dba ? (
                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }} ellipsis={{ tooltip: r.dba }}>
                      DBA: {r.dba}
                    </Text>
                  ) : null}
                </div>
              ) },
              { title: 'Email', dataIndex: 'email', key: 'email', width: 240, ellipsis: { showTitle: true } },
              { title: 'Phone', dataIndex: 'phone', key: 'phone', width: 170, ellipsis: { showTitle: true } },
              { title: 'Address', dataIndex: 'address', key: 'address', width: 260, responsive: ['lg'], ellipsis: { showTitle: true } },
              {
                title: 'Aircraft',
                key: 'aircraft',
                width: 90,
                render: (_, r) => r._count?.aircraft ?? 0,
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 110,
                render: (s: Status) => (
                  <Tag color={s === 'active' ? 'green' : 'default'}>{s}</Tag>
                ),
              },
              {
                title: '',
                key: 'actions',
                width: 120,
                render: (_, r) => (
                  <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                    <Popconfirm
                      title="Delete operator"
                      description={`This will also delete ${r._count?.aircraft ?? 0} aircraft. Bookings are preserved.`}
                      okText="Delete"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => deleteMut.mutate(r.id)}
                    >
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      </Space>

      <Drawer
        title={editing ? `Edit ${editing.name}` : 'New operator'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={520}
        extra={
          <Space>
            <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              loading={submitGuard.busy || createMut.isPending || updateMut.isPending}
              onClick={submit}
            >
              {editing ? 'Save' : 'Create'}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Required' }]}>
            <Input maxLength={LIMITS.name} />
          </Form.Item>
          <Form.Item name="dba" label="DBA">
            <Input maxLength={LIMITS.name} placeholder="Doing business as" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Required' },
              { type: 'email', message: 'Enter a valid email' },
            ]}
          >
            <Input maxLength={LIMITS.email} />
          </Form.Item>
          <Form.Item name="address" label="Address">
            <Input.TextArea maxLength={LIMITS.text} showCount rows={2} />
          </Form.Item>
          <Form.Item name="phone" label="Phone number">
            <Input maxLength={LIMITS.phone} />
          </Form.Item>
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
            />
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
}
